import React from 'react'

import { Space, Text } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { showError } from '#components/Modals'
import { IOrg } from '#state'

import { EmptySeatRows } from '../Members/MemberTable'

import { seatActions } from './seatActions'
import { seatsLeavingAtRenewal, seatsUsed } from './seatMath'
import { SeatWarningBanner } from './SeatWarningBanner'
import { useSeats } from './useSeats'

export type SeatsPanelProps = {
  org: IOrg
  header: React.ReactNode
  table: (emptySeats: Omit<EmptySeatRows, 'onInvite'>) => React.ReactNode
}

export const SeatsPanel = observer(function SeatsPanel({
  org,
  header,
  table,
}: SeatsPanelProps) {
  const { workspace, changes, managePlan } = useSeats(org)
  if (!workspace) return null

  const { removeSeat, keepSeat } = seatActions(
    workspace,
    changes.setReduction.mutateAsync,
  )

  const onManagePlan = async () => {
    const { success, message } = await managePlan()
    if (!success) showError({ message })
  }

  const canManage = workspace.canChangeSeats
  const used = seatsUsed(workspace)

  return (
    <>
      <SeatWarningBanner workspace={workspace} onManagePlan={onManagePlan} />
      {header}
      <Space h="sm" />
      <Text size="sm" c="dimmed">
        {used} of {workspace.seats.licensed}{' '}
        {workspace.seats.licensed === 1 ? 'seat' : 'seats'} used
      </Text>
      <Space h="sm" />
      {table({
        available: workspace.seats.available,
        leaving: seatsLeavingAtRenewal(workspace),
        leavingLabel: workspace.scheduledLabel,
        busy: changes.setReduction.isPending,
        onRemove:
          canManage && !workspace.cancelScheduled ? removeSeat : undefined,
        onKeep: canManage ? keepSeat : undefined,
      })}
    </>
  )
})
